import axios from "axios";
import AlertStore from '@/stores/AlertStore'
import hasPermission from "../filters/hasPermission";
import {
  ENDPOINT_EMPREINTE_ECONOMIQUE, ENDPOINT_EMPREINTE_ECONOMIQUE_REJECT, ENDPOINT_EMPREINTE_ECONOMIQUE_TASKS,
  ENDPOINT_EMPREINTE_ECONOMIQUE_VALIDATE,
  ENDPOINT_EXPORT,
  ENDPOINT_IMPORT,
  ENDPOINT_SEARCH,
} from "../constants/endpoints";
import {
  libelles
} from "../constants/libelles";
import {dateToIsoDateString} from "../utils/dateUtil";

let store = {
  debug: true,

  get(id) {
    return axios
        .get(ENDPOINT_EMPREINTE_ECONOMIQUE + '/' + id)
        .then(response => response.data)
        .catch(error => {
          AlertStore.pushError(error.response.data.message);
        })
  },

  reject(id, commentaire) {
    return axios.post(`${ENDPOINT_EMPREINTE_ECONOMIQUE_REJECT}/${id}`, {
      message: commentaire
    })
      .then((response) => {
        if (response.status === 204) {
          AlertStore.pushSuccess('l\'empreinte a été rejetée avec succès.')
        }
      }).catch((error) => {
        console.log(error)
        AlertStore.pushError('L\'empreinte n\'a pas pu être rejetée.')
      })
  },

  create(empreinte) {
    const formData = new FormData()
    const file = empreinte.pieceJustificative
    delete empreinte.pieceJustificative
    formData.append('data', new Blob([JSON.stringify(empreinte)], {
      type: "application/json"
    }))
    formData.append('file', file)
    return axios
        .post(ENDPOINT_EMPREINTE_ECONOMIQUE, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then((response) => {
          const message = response.data.dispense ? libelles.messageCreateFormulaireWithDispense : 'Empreinte économique créée avec succès.';
          AlertStore.pushSuccess(message);
        })
        .catch(error => {
          if (error.response?.data?.message) {
            AlertStore.pushError(error.response.data.message);
            return;
          }
            AlertStore.pushError("Une erreur est survenue, merci de vérifier que la pièce justificative est au bon format (PDF) et d'un poids inférieur à 5Mo.")
        })
  },

  update(empreinte) {
    return axios
        .put(ENDPOINT_EMPREINTE_ECONOMIQUE, empreinte)
        .then(() => {
          const isAdmin = hasPermission(['ADMINISTRATEUR', 'CORRESPONDANT_EMPREINTE_ADMINISTRATEUR']);
          const correspondantMsg = "Modification d'empreinte économique soumise pour approbation.";
          const adminMsg = "Empreinte économique modifiée avec succès.";
          const successMessage = isAdmin || !empreinte.jourSoumissionPassed ? adminMsg : correspondantMsg;
          AlertStore.pushSuccess(successMessage);
        })
        .catch(error => {
          AlertStore.pushError(error.response.data.message);
        })
  },

  getLatestByEntrepriseIdAndMarcheId(entrepriseId, marcheId) {
    return axios
        .get(ENDPOINT_EMPREINTE_ECONOMIQUE, {params: {entrepriseId, marcheId}})
        .then(response => response.data)
        .catch(error => {
          AlertStore.pushError(error.response.data.message);
        })
  },

  delete(empreinte) {
    return axios
        .delete(ENDPOINT_EMPREINTE_ECONOMIQUE + '/' + empreinte.id)
        .then(() => {
          const isAdmin = hasPermission(['ADMINISTRATEUR', 'CORRESPONDANT_EMPREINTE_ADMINISTRATEUR']);
          const correspondantMsg = "Suppression d'empreinte économique soumise pour approbation.";
          const adminMsg = "Empreinte économique supprimée avec succès.";
          const successMessage = isAdmin || empreinte.statut === 'ATTENTE_MODIFICATION' || !empreinte.jourSoumissionPassed ? adminMsg : correspondantMsg;
          AlertStore.pushSuccess(successMessage);
        })
        .catch(error => {
          AlertStore.pushError(error.response.data.message);
        })
  },

  search(empreinte, pageIndex, pageSize, sortOptions) {
    const searches = this.empreinteToRecherche(empreinte);
    const searchParams = new URLSearchParams()
    searches.forEach((search) => {
      if (search.value instanceof Array) {
        search.value.forEach((v) => {
          searchParams.append(search.key, v)
        })
      } else if (search.value) {
        searchParams.append(search.key, search.value)
      }
    })
    searchParams.append('page', pageIndex)
    searchParams.append('size', pageSize)
    sortOptions?.forEach((sortOption) => {
      searchParams.append('sort', `${sortOption.sortBy},${sortOption.sortDirection}`)
    })
    return axios.get(`${ENDPOINT_EMPREINTE_ECONOMIQUE + ENDPOINT_SEARCH}`, {
      params: searchParams
    })
  },
  export(empreinte) {
    const recherche = this.empreinteToRecherche(empreinte);
    let url = `${ENDPOINT_EMPREINTE_ECONOMIQUE + ENDPOINT_EXPORT}`;
    return axios
        .post(url, recherche);
  },

  importExcel(file) {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(ENDPOINT_EMPREINTE_ECONOMIQUE + ENDPOINT_IMPORT, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  getTasks() {
    return axios.get(ENDPOINT_EMPREINTE_ECONOMIQUE_TASKS)
      .then(response => response.data)
      .catch((error) => AlertStore.pushError(error.response.data.message)
    )
  },

  validate(empreinte) {
    return axios
        .post(ENDPOINT_EMPREINTE_ECONOMIQUE_VALIDATE + '/' + empreinte.id)
        .then(() => {
          const operation = empreinte.statut === 'ATTENTE_MODIFICATION' ? 'modification' : 'suppression';
          AlertStore.pushSuccess(`Demande de ${operation} d'empreinte économique approuvée avec succès.`);
        })
        .catch(error => {
          AlertStore.pushError(error.response.data.message);
        })
  },

  validateAll() {
    return axios
        .post(ENDPOINT_EMPREINTE_ECONOMIQUE_VALIDATE)
        .then(() => {
          AlertStore.pushSuccess(`Formulaires d'empreintes économiques approuvés avec succès.`);
        })
        .catch(error => {
          AlertStore.pushError(error.response.data.message);
        })
  },

  getMontants(entrepriseId, marcheId, date) {
    return axios.get(`${ENDPOINT_EMPREINTE_ECONOMIQUE}/montants`, {
      params: {
        entrepriseId,
        marcheId,
        month: dateToIsoDateString(date)
      }
    })
        .then(response => response.data)
        .catch(error => {
          AlertStore.pushError(error.response.data.message);
        })
  },

  empreinteToRecherche(empreinte) {
    let recherche = [];
    recherche.push({key: 'marche', operation: '=', value: empreinte.marcheId});
    recherche.push({key: 'ligne', operation: '=', value: empreinte.ligneId});
    recherche.push({key: 'statut', operation: '€', value: empreinte.statut});
    recherche.push({key: 'siretEntreprise', operation: ':', value: empreinte.siretEntreprise});
    recherche.push({key: 'nomEntreprise', operation: ':', value: empreinte.nomEntreprise});
    recherche.push({key: 'objetPrestation', operation: ':', value: empreinte.objetPrestation});
    recherche.push({key: 'date', operation: '>=', value: empreinte.dateDebut});
    recherche.push({key: 'date', operation: '<=', value: empreinte.dateFin});
    if (empreinte.dc4 != null) {
      recherche.push({key: 'dc4', operation: '=', value: empreinte.dc4});
    }
    return recherche;
  }

};

export default store;
